import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  cn,
  Flex,
  Icon,
  Progress,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import { StatusDot } from '@/shared/components/StatusDot';

import { EBrowserPreviewFabState } from '../types';

interface IStepControlTooltipProps {
  label: string;
  children: ReactNode;
}

/** Names an icon-only step control or step marker on hover. */
function StepControlTooltip({ label, children }: IStepControlTooltipProps) {
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {/* Lattice's TooltipContent ships z-50; the expanded view's backdrop is z-100. */}
      <TooltipContent side="top" className="z-101">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A step's position on the replay progress bar.
 */
export interface IBrowserStepMarker {
  /** The step's number, unique per session; keys the marker. */
  stepNumber: number;
  /** Where the step starts, as a 0-100 percentage of the recording. */
  position: number;
  /** Where the step starts in the recording, in seconds. */
  seconds: number;
  /** Step description, shown in the marker's tooltip. */
  label: string;
  /** Whether playback has reached this step; fills the dot. */
  reached: boolean;
}

/**
 * Props for the BrowserPreviewFob component.
 */
export interface IBrowserPreviewFabProps {
  /** The current state of the FAB. */
  state?: EBrowserPreviewFabState;
  /** Playback state for the replay play/pause icon. */
  isPlaying?: boolean;
  /** Current timestamp to display (only for replay state). */
  timestamp?: string;
  /** Total recording length, right-aligned opposite the controls (only for replay state). */
  duration?: string;
  /** Playback position as a 0-100 percentage of the recording's duration (only for replay state). */
  progress?: number;
  /** Steps drawn as clickable dots on the progress bar (only for replay state). */
  stepMarkers?: IBrowserStepMarker[];
  /** Seeks the recording to a step marker's start. */
  onStepMarkerClick?: (marker: IBrowserStepMarker) => void;
  /**
   * The session is still live: playback controls are disabled (maximize stays
   * available), the progress bar reads full, and "Live" replaces the time.
   */
  disabled?: boolean;
  /** Whether the screencast has connected. Only matters while `disabled`: gates the "Live" label. */
  connected?: boolean;
  /** Show "Live" while connected. Off when the host shows it by its title. */
  showLiveStatus?: boolean;
  /** Handler for play/pause button click. */
  onPlayPause?: () => void;
  /** Handler for skip back button click (only for replay state). */
  onSkipBack?: () => void;
  /** Handler for skip forward button click (only for replay state). */
  onSkipForward?: () => void;
  /** Handler for maximize/fullscreen button click. */
  onMaximize?: () => void;
  /** Additional CSS classes. */
  className?: string;
}

/**
 * BrowserPreviewFob is the playback control bar below a browser preview.
 *
 * @param props - Component props
 * @returns BrowserPreviewFob component
 *
 * @example
 * // Playing state - shows pause and maximize buttons
 * <BrowserPreviewFob
 *   state={EBrowserPreviewFabState.PLAYING}
 *   onPlayPause={handlePlayPause}
 *   onMaximize={handleMaximize}
 * />
 *
 * // Replay state - shows a progress bar, timestamp and full playback controls
 * <BrowserPreviewFob
 *   state={EBrowserPreviewFabState.REPLAY}
 *   timestamp="0:39"
 *   progress={42}
 *   onPlayPause={handlePlayPause}
 *   onSkipBack={handleSkipBack}
 *   onSkipForward={handleSkipForward}
 *   onMaximize={handleMaximize}
 * />
 */
export function BrowserPreviewFob({
  state = EBrowserPreviewFabState.PLAYING,
  isPlaying: isPlayingProp,
  timestamp = '0:00',
  duration,
  progress = 0,
  stepMarkers,
  onStepMarkerClick,
  disabled = false,
  connected = false,
  showLiveStatus = true,
  onPlayPause,
  onSkipBack,
  onSkipForward,
  onMaximize,
  className,
}: IBrowserPreviewFabProps) {
  const { t } = useTranslation('browser-automation');
  const isPlaying = isPlayingProp ?? state === EBrowserPreviewFabState.PLAYING;
  const isReplay = state === EBrowserPreviewFabState.REPLAY;

  const controls = (
    <Flex
      align="center"
      gap={1}
      className="justify-self-center"
      data-testid="browser-preview-fob-controls"
    >
      {/* Skip Back button - only for replay */}
      {isReplay && (
        <StepControlTooltip label={t('browserAutomation.action.skipBack')}>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            onClick={onSkipBack}
            aria-label={t('browserAutomation.action.skipBack')}
          >
            <Icon type="SkipBack" size="xs" />
          </Button>
        </StepControlTooltip>
      )}

      {/* Play/Pause button */}
      <Button
        variant="ghost"
        size="icon-sm"
        disabled={disabled}
        onClick={onPlayPause}
        aria-label={
          isPlaying
            ? t('browserAutomation.action.pause')
            : t('browserAutomation.action.play')
        }
      >
        <Icon type={isPlaying ? 'Pause' : 'Play'} size="xs" />
      </Button>

      {/* Skip Forward button - only for replay */}
      {isReplay && (
        <StepControlTooltip label={t('browserAutomation.action.skipForward')}>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            onClick={onSkipForward}
            aria-label={t('browserAutomation.action.skipForward')}
          >
            <Icon type="SkipForward" size="xs" />
          </Button>
        </StepControlTooltip>
      )}

      {onMaximize && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onMaximize}
          aria-label={t('browserAutomation.action.maximize')}
        >
          <Icon type="Maximize" size="xs" />
        </Button>
      )}
    </Flex>
  );

  return (
    <Flex direction="column" gap={1.5} className={cn('w-full', className)}>
      {isReplay && (
        // w-full: Lattice's <Flex> doesn't stretch children, so without it the
        // wrapper collapses to zero width and takes the bar with it.
        <div className="relative w-full">
          <Progress
            value={disabled ? 100 : progress}
            className={cn(
              'h-1',
              // `timeupdate` lands roughly every 250ms; gliding linearly over the
              // same span keeps the fill moving between updates. Paused, the only
              // movement is a seek, which eases to the step instead of jumping.
              isPlaying && !disabled
                ? '[&_[data-slot=progress-indicator]]:duration-250 [&_[data-slot=progress-indicator]]:ease-linear'
                : '[&_[data-slot=progress-indicator]]:duration-300 [&_[data-slot=progress-indicator]]:ease-out',
            )}
            aria-label={t('browserAutomation.action.progress')}
          />
          {!disabled &&
            stepMarkers?.map(marker => (
              <StepControlTooltip key={marker.stepNumber} label={marker.label}>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => onStepMarkerClick?.(marker)}
                  aria-label={t('browserAutomation.action.jumpToStep', {
                    label: marker.label,
                  })}
                  data-testid="browser-preview-fob-step-marker"
                  className="group/marker absolute top-1/2 -translate-x-1/2 -translate-y-1/2 hover:bg-transparent"
                  // Position is data-driven; no utility class can express it.
                  style={{ left: `${marker.position}%` }}
                >
                  {/* Card-colored ring separates a passed dot from the same-colored fill. */}
                  <span
                    aria-hidden
                    className={cn(
                      'size-2 rounded-full ring-2 ring-card transition-transform group-hover/marker:scale-125 group-focus-visible/marker:scale-125',
                      marker.reached
                        ? 'bg-primary'
                        : 'border border-muted-foreground bg-card',
                    )}
                  />
                </Button>
              </StepControlTooltip>
            ))}
        </div>
      )}
      {/* Grid, not a centered group + absolute label: on narrow cards the
          duration collided with skip-forward. Fixed side columns avoid that
          while keeping the middle column centered. */}
      <div className="grid min-h-8 w-full grid-cols-[1fr_auto_1fr] items-center gap-1">
        <Flex align="center" gap={1.5} className="justify-self-start px-1">
          {isReplay && disabled && connected && showLiveStatus && (
            <>
              <StatusDot tone="success" pulse />
              <Text level="small" color="muted">
                {t('browserAutomation.action.live')}
              </Text>
            </>
          )}
          {isReplay && !disabled && (
            <Text level="small" color="muted" className="truncate tabular-nums">
              {timestamp}
            </Text>
          )}
        </Flex>
        {controls}
        <Text
          level="small"
          color="muted"
          className="justify-self-end truncate px-1 tabular-nums"
        >
          {isReplay ? duration : null}
        </Text>
      </div>
    </Flex>
  );
}
